import { Card, Tag, Typography, Space, Alert, Steps, Timeline } from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  UserOutlined,
  RightOutlined,
} from '@ant-design/icons'

const { Text } = Typography

interface WorkflowInstanceSummary {
  id: string
  workflowCode: string
  workflowName: string
  label: string | null
  instanceStatus: string
  startedAt: string
  parentInstanceId: string | null
  parentInstanceLabel: string | null
  currentState: {
    id: string
    code: string
    name: string
    parentCode: string | null
    parentName: string | null
    isOverdue: boolean
    enteredAt: string
  } | null
  counts: {
    pendingTasks: number
    appointments: number
    labResults: number
  }
}

interface WorkflowStage {
  code: string
  name: string
  order: number
  icon: string | null
  color: string | null
  status: 'completed' | 'current' | 'pending'
  currentSubState?: string | null
}

interface WorkflowHistoryItem {
  id: string
  fromStateCode: string | null
  fromStateName: string | null
  toStateCode: string
  toStateName: string
  triggeredByRole: string
  triggeredByName: string | null
  reason: string | null
  createdAt: string
}

interface WorkflowDetails {
  instanceId: string | null
  label?: string | null
  workflowCode?: string
  workflowName?: string
  instanceStatus?: string
  startedAt?: string
  currentState: {
    id: string
    code: string
    name: string
    parentCode: string | null
    parentName: string | null
    icon: string | null
    color: string | null
    responsibleRole: string | null
    actionLabel: string | null
    isOverdue: boolean
    dueAt: string | null
    enteredAt: string
  } | null
  availableTransitions: {
    id: string
    toStateCode: string
    toStateName: string
    label: string | null
  }[]
  stages: WorkflowStage[]
  history: WorkflowHistoryItem[]
}

const roleLabels: Record<string, string> = {
  NURSE: 'Sestra',
  DOCTOR: 'Lekar',
  ADMIN: 'Admin',
  PATIENT: 'Pacijent',
  SYSTEM: 'Sistem',
  SUPPORT: 'Podrška',
}

const instanceStatusConfig: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Aktivan', color: 'success' },
  COMPLETED: { label: 'Završen', color: 'default' },
  CANCELLED: { label: 'Otkazan', color: 'error' },
  ON_HOLD: { label: 'Na čekanju', color: 'warning' },
  ARCHIVED: { label: 'Arhiviran', color: 'default' },
}

function formatDate(dateString: string | null) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('sr-Latn-RS', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

interface WorkflowsTabProps {
  workflowInstances: WorkflowInstanceSummary[]
  selectedInstanceId: string | null
  onSelectInstance: (id: string) => void
  workflowDetails: WorkflowDetails | null
  loading: boolean
  detailsLoading: boolean
}

export function WorkflowsTab({
  workflowInstances,
  selectedInstanceId,
  onSelectInstance,
  workflowDetails,
  loading,
  detailsLoading,
}: WorkflowsTabProps) {
  if (workflowInstances.length === 0 && !loading) {
    return (
      <Alert
        message="Nema aktivnih workflow-a"
        description="Ovaj pacijent još nema aktivni workflow. Workflow će se pokrenuti kada pacijent popuni upitnik."
        type="info"
        showIcon
      />
    )
  }

  return (
    <div>
      {/* Instance List */}
      <Card
        title="Aktivni workflow-i"
        style={{ marginBottom: 16 }}
        loading={loading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {workflowInstances.map((inst) => (
            <Card
              key={inst.id}
              size="small"
              style={{
                borderColor: selectedInstanceId === inst.id ? '#1890ff' : undefined,
                cursor: 'pointer',
              }}
              onClick={() => onSelectInstance(inst.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Space style={{ marginBottom: 8 }}>
                    <Text strong>{inst.label || inst.workflowName}</Text>
                    <Tag color={instanceStatusConfig[inst.instanceStatus]?.color || 'default'}>
                      {instanceStatusConfig[inst.instanceStatus]?.label || inst.instanceStatus}
                    </Tag>
                    {inst.currentState?.isOverdue && (
                      <Tag color="error">Zakasnelo</Tag>
                    )}
                  </Space>
                  {inst.currentState && (
                    <div>
                      <Text type="secondary">Trenutno: </Text>
                      <Tag color="processing">{inst.currentState.name}</Tag>
                      {inst.currentState.parentName && (
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          ({inst.currentState.parentName})
                        </Text>
                      )}
                    </div>
                  )}
                </div>
                <Space>
                  {inst.counts.pendingTasks > 0 && (
                    <Tag color="orange">{inst.counts.pendingTasks} zadatak/a</Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Započeto: {formatDate(inst.startedAt)}
                  </Text>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      </Card>

      {/* Selected Instance Details */}
      {selectedInstanceId && workflowDetails?.instanceId && (
        <Card
          title={`Detalji: ${workflowDetails.label || workflowDetails.workflowName || 'Tok tretmana'}`}
          loading={detailsLoading}
        >
          {/* Stepper */}
          {workflowDetails.stages.length > 0 && (
            <Steps
              size="small"
              current={workflowDetails.stages.findIndex(s => s.status === 'current')}
              style={{ marginBottom: 24 }}
              items={workflowDetails.stages.map(stage => ({
                title: stage.name,
                description: stage.status === 'current' && stage.currentSubState
                  ? stage.currentSubState
                  : undefined,
                status: stage.status === 'completed' ? 'finish'
                  : stage.status === 'current' ? 'process'
                  : 'wait',
                icon: stage.status === 'completed' ? <CheckCircleOutlined />
                  : stage.status === 'current' ? <SyncOutlined spin />
                  : <ClockCircleOutlined />,
              }))}
            />
          )}

          {/* Current State */}
          {workflowDetails.currentState && (
            <Alert
              message={
                <Space>
                  <span>Trenutno stanje:</span>
                  <Tag color={workflowDetails.currentState.isOverdue ? 'error' : 'processing'}>
                    {workflowDetails.currentState.name}
                  </Tag>
                  {workflowDetails.currentState.responsibleRole && (
                    <Text type="secondary">
                      (Čeka: {roleLabels[workflowDetails.currentState.responsibleRole] || workflowDetails.currentState.responsibleRole})
                    </Text>
                  )}
                </Space>
              }
              type={workflowDetails.currentState.isOverdue ? 'error' : 'info'}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* History Timeline */}
          {workflowDetails.history.length > 0 && (
            <div>
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                Istorija promena
              </Text>
              <Timeline
                items={workflowDetails.history.map(item => ({
                  color: 'blue',
                  children: (
                    <div>
                      <Space size={4}>
                        {item.fromStateName && (
                          <>
                            <Text type="secondary">{item.fromStateName}</Text>
                            <RightOutlined style={{ fontSize: 10, color: '#999' }} />
                          </>
                        )}
                        <Text strong>{item.toStateName}</Text>
                      </Space>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <UserOutlined style={{ marginRight: 4 }} />
                          {item.triggeredByName || roleLabels[item.triggeredByRole] || item.triggeredByRole}
                          {' • '}
                          {new Date(item.createdAt).toLocaleString('sr-Latn-RS', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Text>
                      </div>
                      {item.reason && (
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                          "{item.reason}"
                        </Text>
                      )}
                    </div>
                  ),
                }))}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
